import React, { useEffect, useState } from 'react';
import { map, merge, Observable, scan, Subject, tap, timer } from 'rxjs';
import { useAPI } from '#/hooks/index.js';
import { createPortal } from 'react-dom';
import styles from './rating.module.css';
import { array, instanceOf, number } from 'prop-types';
import { useStore } from 'react-redux';

Rating.propTypes = {
  emoji$: instanceOf(Observable),
  emojiArray: array,
  interval: number /* 10 min., IMHO too long */,
  history: instanceOf(Map),
};

export function Rating(props) {
  const { emoji$, emojiArray, history, interval = 600 } = props;

  const API = useAPI();
  const store = useStore();

  const [rating, setRating] = useState({
    total: 0,
    counters: new Map(),
  });

  useEffect(() => {
    const state = store.getState();
    const chunkLength = state.emoji.updateInterval;
    const currentPosition = API.timeService.value?.position;
    if (!currentPosition) {
      return;
    }
    const currentChunkStartAt = currentPosition - (currentPosition % chunkLength);
    const fromTimestamp = currentPosition - interval;
    const historySlice = [];
    let chunkIndex = currentChunkStartAt;
    while (chunkIndex >= fromTimestamp) {
      if (history.has(chunkIndex)) {
        historySlice.push(...history.get(chunkIndex));
      }
      chunkIndex -= chunkLength;
    }

    const summarySeed = historySlice
      .filter(
        ([start_time]) =>
          currentPosition - interval <= start_time &&
          start_time <= currentPosition,
      )
      .reduce(
        (acc, [, emoji]) => {
          emoji.forEach((count, name) => {
            if (!acc.counters.has(name)) {
              acc.counters.set(name, 0);
            }
            acc.counters.set(name, acc.counters.get(name) + count);
            acc.total += count;
          });
          return acc;
        },
        {
          counters: new Map(),
          total: 0,
        },
      );

    if (summarySeed.total > 10) {
      setRating(summarySeed);
    }

    /* next block schedule stream for remove from summary history entries, based on deviation currentPostion and actual start_time */
    const historyCleaner$ = new Subject();
    historySlice
      .filter(
        ([start_time]) =>
          currentPosition - interval <= start_time &&
          start_time <= currentPosition,
      )
      .forEach(([start_time, emoji]) => {
        const dec = [];
        emoji.forEach((count, name) => {
          dec.push({
            name,
            count: -count,
          });
        });
        const initialDelay = start_time - fromTimestamp;
        timer(initialDelay * 1e3).subscribe(() => {
          historyCleaner$.next(dec);
        });
      });

    /* next block create stream increment and decrement summary from incoming messages*/
    const dec$ = new Subject();
    const inc$ = emoji$.pipe(
      map(({ positions }) => {
        const currentPosition = API.timeService.value?.position;
        return positions
          .filter(
            /* exclude too far from client position */
            ({ start_time }) => Math.abs(start_time - currentPosition) <= 5,
          )
          .map(({ emoji }) => emoji)
          .flat();
      }),
      tap((message) => {
        timer(interval * 1e3).subscribe(() => {
          const dec = message.map(({ name, count }) => {
            return {
              name,
              count: -count,
            };
          });
          dec$.next(dec);
        });
      }),
    );

    const summary = merge(inc$, dec$, historyCleaner$).pipe(
      scan((acc, curr) => {
        curr.forEach(({ name, count }) => {
          if (!acc.counters.has(name)) {
            acc.counters.set(name, 0);
          }
          acc.counters.set(name, acc.counters.get(name) + count);
          acc.total += count;
        });
        return acc;
      }, summarySeed),
      tap((summary) => {
        console.log('New rating', summary);
      }),
    );

    const subscription = summary.subscribe((rating) => {
      setRating({ ...rating });
    });

    return () => {
      console.log('close subscription');
      subscription.unsubscribe();
    };
  }, []);

  if (rating.total < 10) {
    return null;
  }

  return emojiArray.map(({ name }) => {
    const relatedButton = document.getElementById(`emoji_${name}`);
    return createPortal(
      <EmojiRating rating={rating.counters.get(name)} total={rating.total} />,
      relatedButton,
    );
  });
}

function EmojiRating({ rating, total }) {
  if (!rating) {
    return null;
  }

  return (
    <span className={styles.rating}>{((rating * 100) / total).toFixed()}%</span>
  );
}

EmojiRating.propTypes = {
  rating: number,
  total: number,
};
